import { BuildClass, Do, fsmData } from '../../../../universal'
import { _, React } from '../../../lib'
import { NewSystemFlyoutInstance } from '../../flyouts'
import { FormButton, FormButtonSet } from '../buttons'
import { FormType } from '../form'
import { CJSX } from '../meta-types'
import { NoteInfo, NoteTag, NoteTagGroup, ReducerState } from './types'

/** Get all flattened tags grouped by ID */
export const getFlattenedTags = <T extends Record<string, unknown>>(
	rs: ReducerState<T>,
): Record<string, NoteTag> =>
	_.chain(rs.props.tagging?.options)
		.flatMap(x => {
			if ('GroupName' in x) {
				return x.Tags.map(childTag => ({
					...childTag,
					Colour: childTag.Colour ?? x.Colour,
				}))
			}
			return [x]
		})
		.sortBy(x => x.DisplayName)
		.keyBy(x => x.ID)
		.value()

export const getTagColour = (tag: NoteTag) => hexToHsl(tag.Colour ?? '888888')

const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
	// Unpack the RGB parts
	const rgb = hex.replace(/[^0-9a-f]/g, '')

	// Ensure we have at least 6 digits, pad with zeros if needed
	const normalizedRgb = rgb.padEnd(6, '0')
	const r = parseInt(normalizedRgb.slice(0, 2), 16) / 255
	const g = parseInt(normalizedRgb.slice(2, 4), 16) / 255
	const b = parseInt(normalizedRgb.slice(4, 6), 16) / 255

	// Calculate the lightness
	const max = Math.max(r, g, b)
	const min = Math.min(r, g, b)
	const l = (max + min) / 2

	// Calculate the saturation and hue
	let h = 0
	let s = 0

	if (max !== min) {
		const d = max - min
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
		if (max === r) {
			h = (g - b) / d + (g < b ? 6 : 0)
		} else if (max === g) {
			h = (b - r) / d + 2
		} else {
			h = (r - g) / d + 4
		}
		h /= 6
	}

	// Return the HSL values
	return {
		h: Math.round(h * 360),
		s: Math.round(s * 100),
		l: Math.round(l * 100),
	}
}

const buildHSL = (colour: { h: number; s: number; l: number }) => {
	const h = colour.h
	const s = colour.s
	const l = Math.max(0, Math.min(100, colour.l))
	return `hsl(${h},${s}%,${l}%)`
}

const modifyBrightness = (
	colour: { h: number; s: number; l: number },
	amount: number,
) => ({
	...colour,
	l: Math.max(0, Math.min(100, colour.l + amount)),
})

export const TagIndicator = (props: { tag: NoteTag; includeText: boolean }) => {
	// Get the tag's base colour and use HSL manipulation to get a darker border/shadow
	const colourBase = getTagColour(props.tag)
	const colourTopGrad = modifyBrightness(colourBase, 5)
	const colourBottomGrad = modifyBrightness(colourBase, -5)
	const colourBorder = modifyBrightness(colourBase, -20)
	const colourShadow = modifyBrightness(colourBase, -20)

	// Render
	return (
		<>
			<span className="inline-block h-4 pt-[2px] mx-1 align-middle">
				<span
					className="inline-block h-[10px] w-[10px] rounded-full bg-neutral-500 align-top"
					style={{
						background: `linear-gradient(to bottom, ${buildHSL(colourTopGrad)}, ${buildHSL(colourBottomGrad)})`,
						borderColor: buildHSL(colourBorder),
						boxShadow: `0 0 0 1px ${buildHSL(colourShadow)}`,
					}}
					title={props.tag.DisplayName}
				/>
			</span>
			<CJSX cond={props.includeText}>
				<>{props.tag.DisplayName}</>
			</CJSX>
		</>
	)
}

export const FormFieldNoteTags = <T extends Record<string, unknown>>(
	rs: ReducerState<T>,
) =>
	FormType.DropdownMultiStringKey({
		lbl: 'Tags',
		tooltip:
			'Select one or more tags to help categorise the note. If left empty, the system will apply tentative tags for you to approve based on the content.',
		placeholder: '(Optional)',
		hidden: !rs.props.tagging,
		options: getTagComboboxOptions(rs.props.tagging?.options),
	})

export const getTagComboboxOptions = (options: (NoteTag | NoteTagGroup)[]) => {
	return fsmData(options, {
		filter: x => !(x.NonEditable ?? false),
		sort: x => ('GroupName' in x ? x.GroupName : x.DisplayName),
		map: x => {
			if ('GroupName' in x) {
				return {
					value: null,
					selectable: false,
					text: x.GroupName,
					options: fsmData(x.Tags, {
						filter: y => !(y.NonEditable ?? false),
						sort: y => y.DisplayName,
						map: y => ({
							value: y.ID,
							text: y.DisplayName,
							tooltip: y.Description,
						}),
					}),
				}
			}
			return {
				value: x.ID,
				text: x.DisplayName,
				tooltip: x.Description,
			}
		},
	})
}

export const EditNoteTagsMulti = <T extends Record<string, unknown>>(props: {
	rs: ReducerState<T>
	selected: NoteInfo<T>[]
	flyout?: NewSystemFlyoutInstance
}) => {
	const rs = props.rs

	// Basic form state
	const [loading, setLoading] = React.useState(false)
	const [msg, setMsg] = React.useState('')

	// Get which notes and tags are currently saved
	const tags: Record<string, string[]> = _.chain(props.selected)
		.map(x => x.Tags.map(t => ({ Tag: t, Note: x.ID })))
		.flatten()
		.groupBy(x => x.Tag)
		.mapValues(x => x.map(y => y.Note))
		.value()

	// Track which ones the user has explicitly checked/unchecked
	const [toggledExplicitly, setToggledExplicitly] = React.useState<
		Record<string, boolean>
	>({})
	const isChecked = (tagID: string) =>
		toggledExplicitly[tagID] !== undefined
			? toggledExplicitly[tagID] === true
			: (tags[tagID] ?? []).length == props.selected.length
	const isNotChecked = (tagID: string) =>
		toggledExplicitly[tagID] !== undefined
			? toggledExplicitly[tagID] === false
			: (tags[tagID] ?? []).length == 0
	const isAmbiguous = (tagID: string) =>
		toggledExplicitly[tagID] === undefined &&
		!isChecked(tagID) &&
		!isNotChecked(tagID)

	// Helper function to toggle a tag
	const toggleTag = (tagID: string) => {
		if (toggledExplicitly[tagID] !== undefined) {
			setToggledExplicitly(s => ({ ...s, [tagID]: !s[tagID] }))
		} else {
			setToggledExplicitly(s => ({ ...s, [tagID]: true }))
		}
	}

	// Save handler
	const onSave = () => {
		setLoading(true)
		setMsg('')

		// Identify which tags are being changed
		const additions = _.chain(toggledExplicitly)
			.map((v, k) => ({
				tagID: k,
				isChecked: v,
			}))
			.filter(x => x.isChecked)
			.flatMap(x =>
				props.selected.map(note => ({
					noteID: note.ID,
					tagID: x.tagID,
				})),
			)
			.value()
		const deletions = _.chain(toggledExplicitly)
			.map((v, k) => ({
				tagID: k,
				isChecked: v,
			}))
			.filter(x => !x.isChecked)
			.flatMap(x =>
				props.selected.map(note => ({
					noteID: note.ID,
					tagID: x.tagID,
				})),
			)
			.value()

		// Call the user function to perform the action
		rs.props.tagging?.onUpdate?.({
			additions: additions,
			deletions: deletions,
			onComplete: successOrError => {
				setLoading(false)
				if (successOrError === true) {
					props.flyout?.Close()
				} else {
					setMsg(successOrError)
				}
			},
		})
	}

	// Helper function to render a specific tag line item
	const renderTag = (tag: NoteTag) => {
		// Cache the match type
		const isAmbig = isAmbiguous(tag.ID)
		const isCheck = isChecked(tag.ID)
		const isNotCheck = isNotChecked(tag.ID)

		// Build the tooltip and the ambiguity counts
		const ambiguousCount = (tags[tag.ID] ?? []).length
		const totalCount = props.selected.length
		const tooltip = isAmbig
			? `Only some (${ambiguousCount} / ${totalCount}) of the selected notes currently have this tag. - ${tag.Description}`
			: tag.Description

		// Render
		return (
			<li
				key={tag.ID}
				title={tooltip}
				className={BuildClass({
					'list-none cursor-pointer': true,
					'bg-green-300': isCheck,
					'bg-neutral-300': isAmbig,
					'hover:bg-yellow-100': !isCheck && !isAmbig,
				})}
				onClick={() => {
					toggleTag(tag.ID)
				}}
			>
				<CJSX cond={!isAmbig}>
					<img
						className={BuildClass({
							'inline w-4 h-4 mr-1': true,
							'p-1': isNotCheck,
						})}
						src={Do(() => {
							if (isCheck) {
								return '/static/img/i8/material-outline-checkmark.svg'
							}
							if (isNotCheck) {
								return '/static/img/i8/ios16-cross.svg'
							}
							return null
						})}
					/>
				</CJSX>
				<CJSX cond={isAmbig}>
					<span className="text-center w-4 mr-1 inline-block">•</span>
				</CJSX>
				{tag.DisplayName}
				<CJSX cond={isAmbig}>
					<span className="text-neutral-600 ml-2 text-xs">
						{ambiguousCount} / {totalCount}
					</span>
				</CJSX>
			</li>
		)
	}

	// Render
	return (
		<div className="tailwind-wrapper">
			<ul>
				{fsmData(rs.props.tagging?.options, {
					filter: x => !(x.NonEditable ?? false),
					sort: x => ('GroupName' in x ? x.GroupName : x.DisplayName),
					map: x => {
						if ('GroupName' in x) {
							return (
								<li
									key={`group-${x.GroupName}`}
									className={BuildClass({
										'list-none': true,
									})}
								>
									{x.GroupName}
									<ul>
										{fsmData(x.Tags, {
											filter: y => !(y.NonEditable ?? false),
											sort: y => y.DisplayName,
											map: y => renderTag(y),
										})}
									</ul>
								</li>
							)
						}
						return renderTag(x)
					},
				})}
			</ul>
			<FormButtonSet loading={loading} msg={msg}>
				<FormButton lbl="Save" onClick={onSave} />
				<FormButton
					lbl="Cancel"
					onClick={() => {
						props.flyout?.Close()
					}}
				/>
			</FormButtonSet>
		</div>
	)
}
